/**
 * Face Utilities
 *
 * Helper functions for working with face overlays, including:
 * - Extracting face info from file paths
 * - Calculating face positioning and styles
 */

/**
 * Extract face information from the file path
 * Parses dimensions and positioning from filename format
 */
export const extractFaceInfoFromPath = (path) => {
  try {
    // Path format: "path/to/imageFaceName+440+514+312.png"
    // Where: faceHeightRatio+centerXRatio+centerYRatio (all multiplied by 1000)
    const match = path.match(/\+(\d+)\+(\d+)\+(\d+)\.[^.]+$/);
    if (match && match[1] && match[2] && match[3]) {
      return {
        faceHeightRatio: parseInt(match[1], 10) / 1000, // Convert to decimal ratio
        centerX: parseInt(match[2], 10) / 1000, // Convert to decimal ratio
        centerY: parseInt(match[3], 10) / 1000, // Convert to decimal ratio
      };
    }

    // Fallback to old format if needed
    const oldMatch = path.match(/\+(\d+)\.[^.]+$/);
    if (oldMatch && oldMatch[1]) {
      return {
        faceHeightRatio: parseInt(oldMatch[1], 10) / 1000, // Convert old pixel height to ratio
        centerX: 0.5, // Default - center
        centerY: 0.5, // Default - center
      };
    }
  } catch (error) {
    console.error("Error extracting face info from path:", error);
  }

  // Default if extraction fails - using new ratio format
  return { faceHeightRatio: 0.55, centerX: 0.5, centerY: 0.5 };
};

/**
 * Calculate face style for an image element,
 * calculating the position relative to a given container element.
 *
 * @param {HTMLElement} imgElement - The body image element.
 * @param {HTMLElement} containerElement - The parent container in which the image is placed.
 * @param {Object} bodyImage - The body image data (must include face_position_ratio as relative values).
 * @param {Object} selectedFace - Selected face data.
 * @param {Boolean} showSpecialEffect - Whether to apply a special entrance effect.
 * @param {Object} selectedEffect - Animation effect settings.
 * @param {Number} idx - The index of the body image.
 * @returns {Object|null} - CSS style object for the face overlay.
 */
export const calculateFaceStyle = (
  imgElement,
  containerElement,
  bodyImage,
  selectedFace,
  showSpecialEffect,
  selectedEffect,
  idx
) => {
  if (
    !imgElement ||
    !containerElement ||
    !bodyImage ||
    !bodyImage.face_position_ratio ||
    !selectedFace
  ) {
    console.error("Missing required data for face positioning.");
    return null;
  }

  try {
    // 1. Get body image dimensions relative to viewport
    const imageRect = imgElement.getBoundingClientRect();
    // 2. Get container dimensions (the parent with position: relative)
    const containerRect = containerElement.getBoundingClientRect();

    // 3. Calculate image position within container (relative coordinate system)
    const relativeLeft = imageRect.left - containerRect.left;
    const relativeTop = imageRect.top - containerRect.top;

    // 4. Use natural dimensions to calculate proper scaling
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    const displayedWidth = imageRect.width;
    const displayedHeight = imageRect.height;

    const widthRatio = displayedWidth / naturalWidth;
    const heightRatio = displayedHeight / naturalHeight;
    const scale = Math.min(widthRatio, heightRatio);

    const actualImageWidth = naturalWidth * scale;
    const actualImageHeight = naturalHeight * scale;

    // 5. Calculate offsets based on object-fit: contain and object-position: center bottom
    const offsetX = (displayedWidth - actualImageWidth) / 2;
    const offsetY = displayedHeight - actualImageHeight;

    // 6. Calculate anchor point within container
    const anchorX =
      relativeLeft +
      offsetX +
      bodyImage.face_position_ratio.x * actualImageWidth;
    const anchorY =
      relativeTop +
      offsetY +
      bodyImage.face_position_ratio.y * actualImageHeight;

    // 7. Define face size - using container height for uniform proportions
    const FACE_HEIGHT_PROPORTION = 0.25; // 25% of container height
    const requiredFaceHeight = imageRect.height * FACE_HEIGHT_PROPORTION;

    // 8. Calculate face scaling factor based on original face dimensions
    const originalFaceHeight =
      selectedFace.naturalHeight * selectedFace.faceHeightRatio;
    const faceScale = requiredFaceHeight / originalFaceHeight;
    const scaledFaceWidth = Math.round(selectedFace.naturalWidth * faceScale);
    const scaledFaceHeight = Math.round(selectedFace.naturalHeight * faceScale);

    // 9. Position face so its center (centerX, centerY) aligns with anchor point
    const positionX = anchorX - selectedFace.centerX * scaledFaceWidth;
    const positionY = anchorY - selectedFace.centerY * scaledFaceHeight;

    // 10. Handle animation effects
    const needsSpecialEffect = idx === 0 && showSpecialEffect;
    const animationName = needsSpecialEffect
      ? selectedEffect?.name || "faceAppear"
      : "faceAppear";
    const animationDuration = needsSpecialEffect
      ? selectedEffect?.duration || "0.2s"
      : "0.2s";
    const animationTimingFunction = needsSpecialEffect
      ? selectedEffect?.timing || "ease-out"
      : "ease-out";

    return {
      position: "absolute",
      left: `${positionX}px`,
      top: `${positionY}px`,
      width: `${scaledFaceWidth}px`,
      height: `${scaledFaceHeight}px`,
      backgroundImage: `url(${selectedFace.url})`,
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      zIndex: 150,
      pointerEvents: "none",
      animationName,
      animationDuration,
      animationTimingFunction,
      animationFillMode: "forwards",
    };
  } catch (error) {
    console.error("Error calculating face style:", error);
    return null;
  }
};
